import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useProfile } from './useProfile'
import { supabase } from '../lib/supabase'
import { asJson } from '../lib/db-types'
import { readPreferencesCache, writePreferencesCache } from '../lib/sessionCache'
import { toAppError, type AppError } from '../lib/errors'
import {
  resolvePreferences,
  sidebarItemSchema,
  type Preferences,
} from '../lib/preferencesSchema'

// Re-export the shape types + defaults so existing importers of
// `usePreferences` don't need to change their import paths. The schema
// module is the authoritative definition (ADR 0004); this hook just wires
// it to Supabase.
export { DEFAULTS, DEFAULT_SIDEBAR } from '../lib/preferencesSchema'
export type { Preferences, SidebarItem } from '../lib/preferencesSchema'

export type PreferenceWriteError = AppError

/**
 * Build a migration patch from legacy per-key localStorage entries.
 * Only includes keys that aren't already set in the profile.
 * The legacy keys are deleted by the caller AFTER the RPC succeeds.
 */
function readLegacyMigrationPatch(
  remote: Record<string, unknown> | null | undefined,
): Partial<Preferences> {
  const patch: Partial<Preferences> = {}
  const remoteHas = (k: string) => remote != null && k in remote
  try {
    const t = localStorage.getItem('slash-withdraw-threshold')
    if (t && !remoteHas('withdrawThreshold')) {
      const n = parseInt(t, 10)
      if (Number.isFinite(n)) patch.withdrawThreshold = n
    }
  } catch { /* storage blocked */ }
  try {
    const s = localStorage.getItem('slash-sidebar-config')
    if (s && !remoteHas('sidebarConfig')) {
      const parsed = JSON.parse(s)
      if (Array.isArray(parsed)) {
        patch.sidebarConfig = parsed.flatMap((raw) => {
          const r = sidebarItemSchema.safeParse(raw)
          return r.success ? [r.data] : []
        })
      }
    }
  } catch { /* storage blocked */ }
  return patch
}

function deleteLegacyKeys() {
  try { localStorage.removeItem('slash-withdraw-threshold') } catch { /* storage blocked */ }
  try { localStorage.removeItem('slash-sidebar-config') } catch { /* storage blocked */ }
}

export function usePreferences() {
  const { profile, profileLoading, _setProfilePreferences } = useProfile()
  const userId = profile?.id ?? null

  // Read warm-start cache once per user. Empty if first visit on this device.
  const cachedFallback = useMemo<Partial<Preferences>>(() => {
    if (!userId) return {}
    const cached = readPreferencesCache(userId)
    return (cached && typeof cached === 'object') ? (cached as Partial<Preferences>) : {}
  }, [userId])

  // Resolved preferences = defaults ← cached fallback ← profile.preferences
  const preferences = useMemo(
    () => resolvePreferences(
      profile?.preferences as Record<string, unknown> | undefined,
      cachedFallback,
    ),
    [profile?.preferences, cachedFallback],
  )

  // Keep per-user warm-start cache in sync.
  useEffect(() => {
    if (!profileLoading && profile && userId) {
      writePreferencesCache(userId, preferences)
    }
  }, [preferences, profile, profileLoading, userId])

  // One-shot legacy migration. Pushes legacy localStorage keys into Supabase
  // (so the user's prefs sync on a fresh device) then deletes the legacy keys.
  //
  // Retry cap: the RPC can fail for transient reasons (network blip) but also
  // for permanent ones (malformed legacy values that consistently trip the
  // server's validation). Without a cap, the permanent case re-fires the
  // failing RPC on every tab open for the life of the account — spamming
  // logs and wasting round-trips. We allow up to MIGRATION_MAX_ATTEMPTS
  // retries per userId per session, after which we give up and leave the
  // legacy keys in place for manual investigation. sessionStorage scoping
  // means a fresh tab gets a fresh budget, which is what we want: if a
  // transient issue caused the first session's budget to burn down, the
  // user simply reopening the app clears the block.
  const MIGRATION_MAX_ATTEMPTS = 3
  const migrationAttemptKey = (userId: string) => `fwdslash:pref-migration-attempts:${userId}`
  const migratedForUserRef = useRef<string | null>(null)
  useEffect(() => {
    if (!profile?.id || profileLoading) return
    if (migratedForUserRef.current === profile.id) return
    migratedForUserRef.current = profile.id

    const patch = readLegacyMigrationPatch(profile.preferences as Record<string, unknown> | undefined)
    if (Object.keys(patch).length === 0) {
      // Nothing to push, but if legacy keys still exist, clean them up.
      deleteLegacyKeys()
      return
    }

    // Check / increment attempt budget BEFORE issuing the RPC so a
    // consistently-failing migration burns down the counter rather than
    // hammering indefinitely.
    let attempts = 0
    try {
      attempts = Number(sessionStorage.getItem(migrationAttemptKey(profile.id)) || '0') || 0
    } catch {
      // sessionStorage unavailable (private mode / quota) — fall through
      // and don't retry within-session; the ref guard still prevents
      // within-effect loops and a fresh tab resets the ref.
    }
    if (attempts >= MIGRATION_MAX_ATTEMPTS) {
      console.warn(
        `[Slash] Legacy preference migration: giving up after ${attempts} failed attempts this session. `
        + 'Legacy localStorage keys will be preserved for investigation; reload the tab to retry.',
      )
      return
    }
    try {
      sessionStorage.setItem(migrationAttemptKey(profile.id), String(attempts + 1))
    } catch { /* see above */ }

    supabase.rpc('update_preferences', { patch: asJson(patch) }).then(({ data, error }) => {
      if (error) {
        console.warn(
          `[Slash] Legacy preference migration failed (attempt ${attempts + 1}/${MIGRATION_MAX_ATTEMPTS}):`,
          error.message,
        )
        // Allow the ref gate to reopen so a *different* trigger (e.g. the
        // user toggles a preference and profile.preferences changes, re-
        // running this effect) can try again within the remaining budget.
        migratedForUserRef.current = null
        return
      }
      if (data) _setProfilePreferences(data as Record<string, unknown>)
      deleteLegacyKeys()
      // Success — clear the counter so a future legacy-key reintroduction
      // (e.g. imported from another device) gets a fresh budget.
      try { sessionStorage.removeItem(migrationAttemptKey(profile.id)) } catch { /* */ }
    })
  }, [profile?.id, profile?.preferences, profileLoading, _setProfilePreferences])

  // Optimistic local overrides — while RPC is in flight, show the new value
  const [pendingOverrides, setPendingOverrides] = useState<Partial<Preferences>>({})
  const effective = useMemo(
    () => ({ ...preferences, ...pendingOverrides }),
    [preferences, pendingOverrides],
  )

  // Per-key trailing debounce for rapidly-changing keys (e.g. drag reorder).
  const debounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  // Cleanup pending timers on unmount so writes don't fire post-unmount /
  // post-signOut against a stale auth context.
  useEffect(() => {
    return () => {
      debounceTimers.current.forEach((t) => clearTimeout(t))
      debounceTimers.current.clear()
    }
  }, [])

  const writeToDb = useCallback(async (patch: Partial<Preferences>) => {
    const { data, error } = await supabase.rpc('update_preferences', { patch: asJson(patch) })
    if (!error && data) {
      // Seed query cache directly with the committed value. Avoids flicker
      // between override-clear and the realtime broadcast arriving.
      _setProfilePreferences(data as Record<string, unknown>)
    }
    return error
  }, [_setProfilePreferences])

  const set = useCallback(<K extends keyof Preferences>(
    key: K,
    value: Preferences[K],
    options?: { debounceMs?: number },
  ): Promise<{ error?: PreferenceWriteError }> => {
    setPendingOverrides((prev) => ({ ...prev, [key]: value }))

    return new Promise((resolve) => {
      const flush = async () => {
        const error = await writeToDb({ [key]: value } as Partial<Preferences>)
        setPendingOverrides((prev) => {
          const next = { ...prev }
          delete next[key]
          return next
        })
        if (error) {
          console.error('[Slash] update_preferences failed:', error)
          resolve({ error: toAppError(error) })
        } else {
          resolve({})
        }
      }

      const existing = debounceTimers.current.get(key as string)
      if (existing) clearTimeout(existing)
      const delay = options?.debounceMs ?? 0
      if (delay > 0) {
        debounceTimers.current.set(key as string, setTimeout(() => {
          debounceTimers.current.delete(key as string)
          flush()
        }, delay))
      } else {
        flush()
      }
    })
  }, [writeToDb])

  return {
    preferences: effective,
    loading: profileLoading,
    set,
  }
}
